import { actKey, entryInterestIds } from '@shared/lineupActs'
import { formatDayShort } from '../../../lib/formatDate'
import { setLabel } from '../../../lib/setLabel'
import type { LineupEntry, Stage, Artist } from '../../../types'

const MAX_RESULTS = 30

export function groupBy<T>(arr: T[], key: (item: T) => string): Record<string, T[]> {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const k = key(item)
    ;(acc[k] ??= []).push(item)
    return acc
  }, {})
}

// Same act on multiple days at one stage (or multiple stages on one day)
// produces multiple lineup entries. In a grouped view, render one tile per
// act per group — first entry wins for tier/note display. A solo row and a b2b
// with the same artist are different acts, so both stay.
export function dedupeByAct(entries: LineupEntry[]): LineupEntry[] {
  const seen = new Set<string>()
  return entries.filter(e => {
    const key = actKey(e)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Every name an act answers to: its performers, the collab it plays as, its billing. */
function actNames(e: LineupEntry, artists: Record<string, Artist>): string[] {
  const names = entryInterestIds(e).map(id => artists[id]?.name ?? '')
  return e.title ? [...names, e.title] : names
}

// Lineup search: case-insensitive substring match on any of an act's names
// across every entry (all days/stages), then one card per act, headliners
// (lowest tier) first, then alphabetical. Operates on the already-loaded lineup — no fetch.
export function matchLineupArtists(
  lineup: LineupEntry[],
  artists: Record<string, Artist>,
  needle: string,
): LineupEntry[] {
  const matched = lineup.filter(e =>
    actNames(e, artists).some(n => n.toLowerCase().includes(needle)),
  )
  return dedupeByAct(matched)
    .sort((a, b) => {
      const ta = a.tier ?? 99
      const tb = b.tier ?? 99
      if (ta !== tb) return ta - tb
      return setLabel(a, artists).name.localeCompare(setLabel(b, artists).name)
    })
    .slice(0, MAX_RESULTS)
}

// Per-act context chip for the search results — the day(s) and stage(s) an
// act plays, aggregated across ALL its entries (a deduped result card may
// represent several). Returns a Map keyed by `actKey`; an act with no
// day/stage data is left out so the card falls back to its genres subtitle. Days come pre-localized
// from formatDayShort; everything else is plain data, so no fbt needed here.
export function buildArtistContext(
  lineup: LineupEntry[],
  stages: Stage[],
  locale: string,
): Map<string, string> {
  const stageName = (id: string) => stages.find(s => s.id === id)?.name ?? id
  const byAct = groupBy(lineup, actKey)
  const out = new Map<string, string>()

  for (const [key, entries] of Object.entries(byAct)) {
    const days = [...new Set(entries.map(e => e.day).filter((d): d is string => !!d))].sort()
    const stageIds = [...new Set(entries.map(e => e.stageId).filter((s): s is string => !!s))]

    const dayPart = days.map(d => formatDayShort(d, locale)).join(', ')
    const stagePart = stageIds.map(stageName).join(', ')
    const chip = [dayPart, stagePart].filter(Boolean).join(' · ')
    if (chip) out.set(key, chip)
  }

  return out
}
